import { sanitize } from "@/lib/sanitize";
import { TruncatableDescription } from "@/components/truncatable-description";
import Image from "next/image";
import type { CheckoutSession, LineItem, Market } from "@/lib/api/checkout.schema";
import { normaliseMarkets, type MarketPreview } from "@/lib/markets";
import { MarketSelect } from "@/components/market-select";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/text";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SecuredBy } from "./secured-by";
import { formatAmount } from "./payment.utils";
import { createSessionAmountFormatter } from "./session-amounts";
import type { Dictionary } from "../../../dictionaries";

interface OrderSummaryProps {
  session: CheckoutSession;
  dictionary: Dictionary;
  customAmount?: number;
  onAmountChange?: (amount: number | undefined) => void;
  /** Market whose currency prices are previewed in */
  previewMarket?: Market | null;
  onPreviewMarketChange?: (country: string) => void;
  /** Indicative conversion for the previewed market, once quoted */
  preview?: MarketPreview | null;
}

function LineItemCompact({
  item,
  formatPrice,
  isLast,
}: {
  item: LineItem;
  formatPrice: (amount: number) => string;
  isLast: boolean;
}) {
  const imageUrl = item.image_url || item.image;
  const initials = item.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div>
      <div className="flex items-center gap-3 py-3.5">
        <Avatar className="size-9 rounded-md md:size-10">
          {imageUrl && <AvatarImage src={imageUrl} alt={item.name} />}
          <AvatarFallback className="rounded-md bg-muted text-xs">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-1 items-center justify-between gap-2">
          <div className="flex-1 min-w-0 max-w-[180px] md:max-w-none">
            <Text variant="small" className="break-words">
              {item.name}
            </Text>
            {item.description && (
              <div
                className="text-xs text-muted-foreground break-words line-clamp-2 [&_p]:m-0"
                dangerouslySetInnerHTML={{
                  __html: sanitize(item.description),
                }}
              />
            )}
          </div>
          <Text variant="small" className="shrink-0">
            {formatPrice(item.total ?? item.quantity * item.unit_price)}
          </Text>
        </div>
      </div>
      {!isLast && <Separator />}
    </div>
  );
}

function LineItemCard({
  item,
  formatPrice,
}: {
  item: LineItem;
  formatPrice: (amount: number) => string;
}) {
  const imageUrl = item.image_url || item.image;
  const initials = item.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {imageUrl ? (
        <div className="relative aspect-square w-full bg-muted">
          <Image
            src={imageUrl}
            alt={item.name}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      ) : (
        <div className="flex aspect-square w-full items-center justify-center bg-muted">
          <span className="text-2xl font-semibold text-muted-foreground">
            {initials}
          </span>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Text variant="small" className="font-medium break-words">
              {item.name}
            </Text>
            {item.description && (
              <div
                className="mt-0.5 text-xs text-muted-foreground break-words line-clamp-2 [&_p]:m-0"
                dangerouslySetInnerHTML={{
                  __html: sanitize(item.description),
                }}
              />
            )}
          </div>
          <Text variant="small" className="shrink-0 font-medium">
            {formatPrice(item.total ?? item.quantity * item.unit_price)}
          </Text>
        </div>
      </div>
    </div>
  );
}

export function OrderSummary({
  session,
  dictionary,
  customAmount,
  onAmountChange,
  previewMarket = null,
  onPreviewMarketChange,
  preview = null,
}: OrderSummaryProps) {
  const { checkout } = dictionary;

  const lineItems = session.line_items;
  const description = session.description;
  const isCustomAmount = session.allow_custom_amount === true && onAmountChange;
  const minAmount = session.min_amount ?? 0;
  const maxAmount = session.max_amount ?? 0;
  const lineItemsStyle = session.display?.line_items_style ?? "compact";

  // When display_currency is set, render amounts in it. The custom-amount
  // input stays in session.currency since that is what the backend charges.
  const { formatPrice, formatFixedAmount } =
    createSessionAmountFormatter(session, preview);
  // One market means there is nothing to switch between
  const markets = normaliseMarkets(session.markets);
  const marketSelect =
    markets.length > 1 && previewMarket && onPreviewMarketChange ? (
      <div className="mb-4">
        <Label htmlFor="checkout-currency" className="mb-1.5 font-normal text-muted-foreground">
          {dictionary.paymentPage.currency}
        </Label>
        <MarketSelect
          id="checkout-currency"
          variant="field"
          markets={markets}
          value={previewMarket}
          onChange={onPreviewMarketChange}
          label={dictionary.paymentPage.currency}
        />
      </div>
    ) : null;

  const amountError =
    isCustomAmount && customAmount !== undefined
      ? customAmount < minAmount
        ? `${checkout.minimumAmount} ${formatAmount(minAmount, session.currency)}`
        : maxAmount > 0 && customAmount > maxAmount
          ? `${checkout.maximumAmount} ${formatAmount(maxAmount, session.currency)}`
          : null
      : null;

  return (
    <div className="flex h-full flex-col">
      {/* Amount */}
      <div>
        {isCustomAmount ? (
          <>
            <Text variant="muted" className="mb-1.5">
              {checkout.amount}
            </Text>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                {session.currency}
              </span>
              <Input
                type="number"
                inputMode="numeric"
                placeholder={checkout.enterAmount}
                value={customAmount ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  onAmountChange(val === "" ? undefined : Number(val));
                }}
                min={minAmount}
                max={maxAmount}
                aria-label={checkout.amount}
                aria-invalid={!!amountError}
                className="h-11 pl-12 tabular-nums"
              />
            </div>
            {amountError ? (
              <Text variant="muted" className="mt-1.5 text-xs text-destructive">
                {amountError}
              </Text>
            ) : (
              <Text variant="muted" className="mt-1.5 text-xs">
                {checkout.between} {formatAmount(minAmount, session.currency)}{" "}
                {checkout.and} {formatAmount(maxAmount, session.currency)}
              </Text>
            )}
          </>
        ) : (
          <>
            {marketSelect}
            <Text variant="muted" className="mb-1.5">
              {checkout.pay}
            </Text>
            <Text
              variant="h2"
              className="border-0 pb-0 text-3xl font-semibold tracking-tight tabular-nums"
            >
              {formatFixedAmount()}
            </Text>
          </>
        )}
      </div>

      {/* Description and Line items */}
      <div className="mt-6">
        {description && (
          <>
            <Text variant="muted" className="mb-1">
              {checkout.for}
            </Text>
            <div className="mb-3">
              <TruncatableDescription
                html={description}
                readMoreLabel={dictionary.paymentPage?.readMore ?? "Read more"}
                readLessLabel={dictionary.paymentPage?.readLess ?? "Read less"}
                proseClassName="prose prose-sm prose-muted max-w-none leading-relaxed [&>p]:my-3 [&>div]:my-3 [&_a]:text-blue-600 [&_a]:underline prose-strong:text-foreground"
              />
            </div>
          </>
        )}

        {lineItems && lineItems.length > 0 && (
          lineItemsStyle === "cards" ? (
            <div className="flex flex-col gap-3">
              {lineItems.map((item, index) => (
                <LineItemCard
                  key={item.id || index}
                  item={item}
                  formatPrice={formatPrice}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col">
              {lineItems.map((item, index) => (
                <LineItemCompact
                  key={item.id || index}
                  item={item}
                  formatPrice={formatPrice}
                  isLast={index === lineItems.length - 1}
                />
              ))}
            </div>
          )
        )}
      </div>

      {/* Footer - desktop only */}
      <SecuredBy
        className="mt-auto hidden pt-6 text-left lg:block"
        dictionary={dictionary}
      />
    </div>
  );
}
